import React, { memo, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { RootState } from 'redux/store';
import NotificationCard from 'components/NotificationCard/Index';
import Image from '../image/Image';
import Button from '../Button/Index';
import ProfileImage from 'components/ProfileImage';
import useLocales from 'hooks/useLocales';
import { headerDropdownItems } from 'core/constants/header';
import { CompanyService } from 'services/companes/companies';
import Popup from 'components/shared/Popups/Popup/Index';
import SettingsPopup from 'components/shared/Popups/Settings/Index';
import { headerDropdownItemsEnum } from 'core/enum/header-dropdown';
import useLanguage from 'hooks/useLanguage';
import { AuthService } from 'services/auth/auth';
import { NavbarActionEnum } from 'core/enum/navbar-action';
import { NotificationService } from 'services/notifications/notifications';
import DefaultImage from 'components/DefaultImage/Index';
import { IMe } from 'core/interface/me';
import UpgradePlanPopup from 'components/shared/Popups/UpgradePlan/Index';
import { CompanyTabEnum } from 'core/enum/companyTab';
import { clearAllState } from '../../redux/actions/clearState';

import './index.scss';
import { useTenantInfo } from 'hooks/useTenantInfo';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const { t, onChangeLang } = useLocales();
  const me = useSelector((state: IMe) => state.user).user;
  const isLoggedIn = !!me;
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showUpgrade, setShowUpgrade] = useState<boolean>(false);
  const [showDropdown, setShowDropDown] = useState<boolean>(false);
  const { tenantLogo, tenantSiteLanguages } = useTenantInfo();
  const { LANGS } = useLanguage(tenantSiteLanguages);

  const [showAction, setShowAction] = useState<string>(
    NavbarActionEnum.SETTINGS
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { unReadCount }: any = useSelector(
    (state: RootState) => state.notifications
  );

  useEffect(() => {
    if (isLoggedIn) {
      CompanyService.getCompanies(me?.company);
      getAllNotifications();
    }
  }, [isLoggedIn, me]);

  useEffect(() => {
    if (isLoggedIn) {
      getNotifications();
    }
  }, [unReadCount, isLoggedIn]);

  const toggleDropDown = (
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ): void => {
    e.stopPropagation();
    setShowAction(NavbarActionEnum.SETTINGS);
    setShowDropDown((prev) => !prev);
  };

  const handleLogOut = async (): Promise<void> => {
    try {
      // First clear all Redux state
      clearAllState();

      // Then perform logout
      await AuthService.logOut();

      // Finally remove the token and navigate
      Cookies.remove('accesstoken');
      localStorage.setItem('activeTab', CompanyTabEnum.FIND_CANDIDATES);
      navigate('/login');
    } catch (error) {
      console.log(error);
    }
  };

  const getNotifications = (): void => {
    NotificationService.getUnreadNotifications();
  };

  const getAllNotifications = (): void => {
    NotificationService.getAllNotifications();
  };

  const handleMembership = () => {
    navigate('/membership');
  };

  const handleDropdownClick = (title: string): void => {
    switch (title.toLocaleLowerCase()) {
      case t(headerDropdownItemsEnum.SETTINGS).toLowerCase():
        setShowSettings(true);
        break;
      case t(headerDropdownItemsEnum.MEMBERSHIP).toLowerCase():
        handleMembership();
        break;
      case t(headerDropdownItemsEnum.SIGN_OUT).toLowerCase():
        handleLogOut();
        break;
      case t(headerDropdownItemsEnum.UPGRADE).toLowerCase():
        setShowUpgrade(true);
        break;
    }
  };

  const handleClosePopup = async (): Promise<void> => {
    setShowSettings(false);
    setShowUpgrade(false);
    AuthService.getAccount();
  };

  const handleLanguageChange = (language: string) => {
    onChangeLang(language);
  };

  const handleOpenNotifications = (
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ): void => {
    e.stopPropagation();
    setShowAction(NavbarActionEnum.NOTIFICATIONS);
    setShowDropDown((prev) => !prev);
    getAllNotifications();
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        showDropdown &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropDown(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [showDropdown]);

  const dropdownItems =
    me?.role === 'admin'
      ? headerDropdownItems(t)
      : headerDropdownItems(t).filter(
          (item) => item.key !== headerDropdownItemsEnum.MEMBERSHIP
        );

  const navigateToPublicJobs = () => {
    navigate('/jobs');
  };

  const navigateToPublicCompanies = () => {
    navigate('/companies');
  };

  const navigateToLogin = () => {
    navigate('/login');
  };

  const navigateToSignup = () => {
    navigate('/signup');
  };

  return (
    <nav>
      <div
        className="navbar"
        style={{
          marginBottom:
            useLocation().pathname.includes('/jobs') ||
            useLocation().pathname.includes('/companies')
              ? '0px'
              : '25px'
        }}
      >
        <a href="/" className="navbar-logo">
          {tenantLogo ? (
            <Image url={tenantLogo} width={210} />
          ) : (
            <Image name="new-rec-logos.svg" width={210} height={95} />
          )}
        </a>
        <div className="navbar__left">
          <div className="navbar__navigation">
            <div className="nav-link" onClick={navigateToPublicCompanies}>
              {t('Companies')}
            </div>
          </div>

          <div className="navbar__navigation" style={{ marginRight: '20px' }}>
            <div className="nav-link" onClick={navigateToPublicJobs}>
              {t('Jobs')}
            </div>
          </div>
          {isLoggedIn ? (
            <>
              {me?.role === 'admin' && (
                <Button
                  onClick={() => setShowUpgrade(true)}
                  t={t}
                  className="navbar__left-btn"
                  title="Upgrade"
                />
              )}
              <div className="navbar__left__dropdown">
                <div
                  onClick={(e) => toggleDropDown(e)}
                  className="navbar__left__dropdown-btn flex items-center justify-between"
                >
                  <span onClick={handleOpenNotifications}>
                    {me.avatar ? (
                      <ProfileImage
                        notificationCount={
                          unReadCount?.unread_count === 0
                            ? ''
                            : unReadCount?.unread_count
                        }
                        objectFit="cover"
                        height={44}
                        width={44}
                        name="profile.svg"
                        url={me.avatar}
                      />
                    ) : (
                      <DefaultImage
                        className="default-img"
                        height={44}
                        width={44}
                        letter={me?.first_name}
                      />
                    )}
                  </span>

                  <span className="navbar__left__dropdown-name">
                    {me.name ? me.name : ''}
                  </span>
                  <span>
                    <Image
                      className={`navbar__left__dropdown-arrow ${
                        showDropdown && showAction === NavbarActionEnum.SETTINGS
                          ? 'active'
                          : ''
                      }`}
                      name="arrow.svg"
                    />
                  </span>
                </div>
                {showDropdown && (
                  <>
                    <div
                      ref={dropdownRef}
                      className={`navbar__left__dropdown__content ${
                        showAction === NavbarActionEnum.NOTIFICATIONS
                          ? 'notification'
                          : ''
                      }`}
                    >
                      {showAction === NavbarActionEnum.SETTINGS && (
                        <div className="navbar__left__dropdown__content__cards">
                          <ul className="navbar__left__dropdown__content__cards__ul">
                            {dropdownItems.map((item, index) => (
                              <li
                                onClick={() => handleDropdownClick(item.title)}
                                key={index}
                                className={`navbar__left__dropdown__content__cards__ul__li ${
                                  item.title === headerDropdownItemsEnum.UPGRADE
                                    ? 'upgrade-li'
                                    : ''
                                }`}
                              >
                                <Image
                                  name={item.icon}
                                  width={16}
                                  height={16}
                                />
                                <span className="navbar__left__dropdown__content__cards__ul__li-title">
                                  {item.title}
                                </span>
                              </li>
                            ))}

                            {LANGS.length > 1 && (
                              <li className="navbar__left__dropdown__content__cards__ul__li lang-li">
                                {LANGS.map((language, index) => (
                                  <div
                                    onClick={() =>
                                      handleLanguageChange(language.value)
                                    }
                                    key={index}
                                    className="navbar__left__dropdown__content__cards__ul__li__lang"
                                  >
                                    <Image
                                      name={language.icon1}
                                      width={16}
                                      height={16}
                                    />
                                    <span className="navbar__left__dropdown__content__cards__ul__li-title">
                                      {t(language.label)}
                                    </span>
                                  </div>
                                ))}
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                      {showAction === NavbarActionEnum.NOTIFICATIONS && (
                        <NotificationCard />
                      )}
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="navbar__auth">
              <div className="navbar__auth-buttons">
                <Button
                  onClick={navigateToLogin}
                  t={t}
                  className="navbar__auth-login"
                  title="Login"
                />
                <Button
                  onClick={navigateToSignup}
                  t={t}
                  className="navbar__auth-signup"
                  title="Sign Up"
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {showSettings && <SettingsPopup handleClosePopup={handleClosePopup} />}
      {showUpgrade && (
        <Popup
          className="upgrade-plan-nav upgrade-popup-nav"
          title={t('Upgrade your plan')}
          description={t('Your subscription plan determines how many profiles you can unlock and how many new jobs you can post each month. Once you use up these credits, they won’t be replenished - even if you delete a job post. Any unused credits at the end of your billing cycle will roll over to the next month, so you don’t lose them.')}
          onClose={handleClosePopup}
        >
          <UpgradePlanPopup t={t} />
        </Popup>
      )}
    </nav>
  );
};

export default memo(Navbar);
